<template>
  <b-card>
    <div class="d-flex justify-content-between align-items-center mb-1">
      <h4 class="mb-0">
        Мои задачи
      </h4>
      <b-button
        size="sm"
        variant="outline-primary"
        @click="load"
      >
        <feather-icon icon="RefreshCwIcon" /> Обновить
      </b-button>
    </div>

    <!-- Аналитика: счётчики-фильтры -->
    <b-row class="mb-1">
      <b-col
        v-for="c in counters"
        :key="c.key"
        cols="6"
        md="3"
        class="mb-50"
      >
        <div
          class="border rounded p-1 text-center cursor-pointer"
          :class="quick === c.key ? `bg-light-${c.variant} border-${c.variant}` : ''"
          @click="quick = quick === c.key ? 'all' : c.key"
        >
          <div
            class="font-large-1 font-weight-bolder"
            :class="`text-${c.variant}`"
          >
            {{ c.value }}
          </div>
          <small class="text-muted">{{ c.label }}</small>
        </div>
      </b-col>
    </b-row>

    <!-- Фильтры -->
    <div class="d-flex flex-wrap align-items-center mb-1">
      <b-form-select
        v-model="procFilter"
        size="sm"
        class="mr-1 mb-50"
        style="width: 200px"
        :options="procOptions"
      />
      <b-form-select
        v-model="statusFilter"
        size="sm"
        class="mr-1 mb-50"
        style="width: 180px"
        :options="statusOptions"
      />
      <b-button
        v-if="procFilter || statusFilter || quick !== 'all'"
        size="sm"
        variant="flat-secondary"
        class="mb-50"
        @click="resetFilters"
      >
        <feather-icon icon="XIcon" /> Сбросить
      </b-button>
    </div>

    <b-table
      responsive
      small
      hover
      :busy="loading"
      :fields="fields"
      :items="filteredTasks"
      :per-page="perPage"
      :current-page="currentPage"
      sort-by="deadline"
      show-empty
      :empty-text="tasks.length ? 'Нет задач по фильтру' : 'Нет назначенных задач'"
    >
      <template #table-busy>
        <div class="text-center text-primary my-2">
          <b-spinner small class="align-middle mr-50" /> Загрузка…
        </div>
      </template>
      <template #cell(status_code)="data">
        <b-badge :variant="cubus.bpmStatusVariant(data.value)">
          {{ cubus.bpmStatusLabel(data.value) }}
        </b-badge>
      </template>
      <template #cell(deadline)="data">
        <b-badge
          v-if="data.item.deadline"
          :variant="data.item.overdue ? 'danger' : 'light-secondary'"
        >
          {{ data.item.deadline }}{{ data.item.overdue ? ' · просрочено' : '' }}
        </b-badge>
        <span
          v-else
          class="text-muted"
        >—</span>
      </template>
      <template #cell(actions)="data">
        <b-button
          size="sm"
          variant="primary"
          class="mr-50"
          @click="openTask(data.item.id)"
        >
          Открыть
        </b-button>
        <b-button
          size="sm"
          variant="outline-secondary"
          @click="openDelegate(data.item)"
        >
          <feather-icon icon="Share2Icon" /> Передать
        </b-button>
      </template>
    </b-table>
    <b-pagination
      v-if="filteredTasks.length > perPage"
      v-model="currentPage"
      :total-rows="filteredTasks.length"
      :per-page="perPage"
      align="center"
      size="sm"
      class="mt-1"
    />

    <b-modal
      v-model="delegate.show"
      title="Передать задачу"
      centered
      hide-footer
    >
      <p class="mb-1">
        Задача: <b>{{ delegate.task && delegate.task.label }}</b>
      </p>
      <b-form-group label="Кому передать">
        <b-form-select
          v-model="delegate.toSid"
          size="sm"
          :options="userOptions"
        />
      </b-form-group>
      <div class="d-flex justify-content-end">
        <b-button
          variant="outline-secondary"
          class="mr-1"
          @click="delegate.show = false"
        >
          Отмена
        </b-button>
        <b-button
          variant="primary"
          :disabled="delegate.busy || !delegate.toSid"
          @click="submitDelegate"
        >
          Передать
        </b-button>
      </div>
    </b-modal>
  </b-card>
</template>

<script>
import {
  BCard, BTable, BButton, BBadge, BSpinner, BRow, BCol,
  BFormSelect, BModal, BFormGroup, BPagination,
} from 'bootstrap-vue'
import useJwt from '@/cubus/jwt/useJwt'
import useCubus from '@/cubus/services/useCubus'

export default {
  name: 'CbsBpmInbox',
  components: {
    BCard, BTable, BButton, BBadge, BSpinner, BRow, BCol,
    BFormSelect, BModal, BFormGroup, BPagination,
  },
  data() {
    return {
      cubus: useCubus,
      loading: false,
      currentPage: 1,
      perPage: 10,
      tasks: [],
      users: [],
      quick: 'all',
      procFilter: '',
      statusFilter: '',
      delegate: { show: false, busy: false, task: null, toSid: null },
      fields: [
        { key: 'proc_name', label: 'Процесс', sortable: true },
        { key: 'label', label: 'Задача' },
        { key: 'status_code', label: 'Статус', sortable: true },
        { key: 'deadline', label: 'Срок', sortable: true },
        { key: 'initiator', label: 'Инициатор' },
        { key: 'actions', label: '' },
      ],
    }
  },
  computed: {
    today() { return new Date().toISOString().slice(0, 10) },
    isToday() { return t => t.deadline && String(t.deadline).slice(0, 10) === this.today },
    counters() {
      return [
        { key: 'all', label: 'Всего', value: this.tasks.length, variant: 'primary' },
        { key: 'overdue', label: 'Просрочено', value: this.tasks.filter(t => t.overdue).length, variant: 'danger' },
        { key: 'today', label: 'Сегодня', value: this.tasks.filter(this.isToday).length, variant: 'warning' },
        { key: 'in_progress', label: 'В работе', value: this.tasks.filter(t => t.status_code === 'IN_PROGRESS').length, variant: 'info' },
      ]
    },
    procOptions() {
      const names = [...new Set(this.tasks.map(t => t.proc_name).filter(Boolean))]
      return [{ value: '', text: 'Все процессы' }].concat(names.map(n => ({ value: n, text: n })))
    },
    statusOptions() {
      const codes = [...new Set(this.tasks.map(t => t.status_code).filter(Boolean))]
      return [{ value: '', text: 'Все статусы' }].concat(codes.map(c => ({ value: c, text: useCubus.bpmStatusLabel(c) })))
    },
    userOptions() {
      return [{ value: null, text: '— выберите исполнителя —' }].concat(
        this.users.map(u => ({ value: u.sid, text: u.name || u.username || u.sid })),
      )
    },
    filteredTasks() {
      return this.tasks.filter(t => {
        if (this.procFilter && t.proc_name !== this.procFilter) return false
        if (this.statusFilter && t.status_code !== this.statusFilter) return false
        if (this.quick === 'overdue' && !t.overdue) return false
        if (this.quick === 'today' && !this.isToday(t)) return false
        if (this.quick === 'in_progress' && t.status_code !== 'IN_PROGRESS') return false
        return true
      })
    },
  },
  watch: {
    procFilter() { this.currentPage = 1 },
    statusFilter() { this.currentPage = 1 },
    quick() { this.currentPage = 1 },
  },
  created() {
    useCubus.setPageTitle('Мои задачи')
    this.load()
    this.loadUsers()
  },
  methods: {
    load() {
      this.loading = true
      useJwt.query({
        token: useJwt.getCubusToken(),
        query: { method: 'bpm_inbox', param: {}, mode: 'sync' },
      })
        .then(response => {
          this.loading = false
          if (response.data.error) useCubus.toastError(this, response.data.error)
          else this.tasks = (response.data.result && response.data.result.tasks) || []
        })
        .catch(error => { this.loading = false; useCubus.toastError(this, error) })
    },
    loadUsers() {
      useJwt.query({
        token: useJwt.getCubusToken(),
        query: { method: 'bpm_users', param: {}, mode: 'sync' },
      })
        .then(response => { if (!response.data.error) this.users = (response.data.result && response.data.result.users) || [] })
        .catch(() => {})
    },
    resetFilters() {
      this.procFilter = ''
      this.statusFilter = ''
      this.quick = 'all'
    },
    openTask(si) {
      this.$router.push({ name: 'cubus-bpm-task', params: { si: String(si) } })
    },
    openDelegate(task) {
      this.delegate = { show: true, busy: false, task, toSid: null }
    },
    submitDelegate() {
      if (!this.delegate.toSid) return
      this.delegate.busy = true
      useJwt.query({
        token: useJwt.getCubusToken(),
        query: { method: 'bpm_delegate', param: { stage_instance_id: this.delegate.task.id, to_sid: this.delegate.toSid }, mode: 'sync' },
      })
        .then(response => {
          this.delegate.busy = false
          if (response.data.error) { useCubus.toastError(this, response.data.error); return }
          useCubus.toastMessage(this, 'Задача передана')
          this.delegate.show = false
          this.load()
        })
        .catch(error => { this.delegate.busy = false; useCubus.toastError(this, error) })
    },
  },
}
</script>
